"use client";

import * as React from "react";
import { MoreHorizontal } from "lucide-react";

import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

type Item = { key: string; node: React.ReactNode };

function useResizeObserverWidth(ref: React.RefObject<HTMLElement>) {
  const [w, setW] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!ref.current) return;

    const el = ref.current;
    const ro = new ResizeObserver(() => setW(el.getBoundingClientRect().width));
    ro.observe(el);
    setW(el.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, []);

  return w;
}

export function ResponsiveToolbarRow({ items, rightSlot, className }: { items: Item[]; rightSlot?: React.ReactNode; className?: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  //@ts-ignore
  const containerW = useResizeObserverWidth(containerRef);

  const rightSlotRef = React.useRef<HTMLDivElement>(null);
  const [rightSlotW, setRightSlotW] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!rightSlotRef.current) return;
    setRightSlotW(rightSlotRef.current.getBoundingClientRect().width);
  }, [rightSlot]);

  const widthsRef = React.useRef<Record<string, number>>({});
  const [, force] = React.useReducer((x) => x + 1, 0);

  const setWidth = React.useCallback((key: string, w: number) => {
    if (widthsRef.current[key] !== w) {
      widthsRef.current[key] = w;
      force();
    }
  }, []);

  const MORE_W = 80;
  const GAP = 4;
  const PADDING_RESERVE = 8;

  const { visible, overflow } = React.useMemo(() => {
    const widths = widthsRef.current;

    if (!containerW) return { visible: items, overflow: [] as Item[] };

    const rightReserve = rightSlot ? rightSlotW : 0;
    const overflowReserve = MORE_W + GAP * 4;
    let used = rightReserve + overflowReserve + PADDING_RESERVE;

    let total = used;
    for (const it of items) total += (widths[it.key] ?? 0) + GAP;
    if (total <= containerW) return { visible: items, overflow: [] as Item[] };

    const vis: Item[] = [];
    const over: Item[] = [];

    for (const it of items) {
      const w = widths[it.key] ?? 0;
      const needed = vis.length > 0 ? w + GAP : w;
      if (used + needed <= containerW) {
        vis.push(it);
        used += needed;
      } else {
        over.push(it);
      }
    }

    return { visible: vis, overflow: over };
  }, [containerW, items, rightSlot]);

  return (
    <div id="responsiveGroupToolbar" className={"relative w-full " + (className ?? "")}>
      <div aria-hidden="true" className="absolute left-0 top-0 flex gap-1" style={{ visibility: "hidden", pointerEvents: "none" }}>
        {items.map((it) => (
          <Measured key={it.key} onWidth={(w) => setWidth(it.key, w)}>
            {it.node}
          </Measured>
        ))}
      </div>

      <div ref={containerRef} className="flex w-full items-center gap-1">
        {visible.map((it) => (
          <React.Fragment key={it.key}>{it.node}</React.Fragment>
        ))}

        <div className="grow" />

        <div ref={rightSlotRef}>{rightSlot}</div>

        {overflow.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="shrink-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="p-2">
              <div className="flex flex-col gap-1">
                {overflow.map((it) => (
                  <div key={it.key}>{it.node}</div>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function Measured({ onWidth, children }: { onWidth: (w: number) => void; children: React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (!ref.current) return;

    const el = ref.current;
    const ro = new ResizeObserver(() => {
      onWidth(el.getBoundingClientRect().width);
    });

    ro.observe(el);
    onWidth(el.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, [onWidth]);

  return (
    <div ref={ref} className="shrink-0">
      {children}
    </div>
  );
}
